/**
 * Centralized analytics for the amb landing funnel.
 *
 * Destinations:
 * - Microsoft Clarity (custom events + session tags) — REACT_APP_CLARITY_PROJECT_ID
 * - Google Tag Manager dataLayer (GTM-M2N4B5N in index.html)
 * - GA4 via gtag when present
 *
 * Clarity setup: set REACT_APP_CLARITY_PROJECT_ID at build time or in npm start.
 * GTM can also load Clarity; both paths are safe (init is idempotent).
 */

export const FunnelSteps = {
  LANDING_VIEW: 'landing_view',
  CTA_CLICKED: 'cta_clicked',
  MAP_SECTION_VIEWED: 'map_section_viewed',
  HOW_IT_WORKS_VIEWED: 'how_it_works_viewed',
  MAP_INTERACTION_STARTED: 'map_interaction_started',
  MAP_EXPANDED: 'map_expanded',
  MAP_COLLAPSED: 'map_collapsed',
  LOCATION_SEARCH_OPENED: 'location_search_opened',
  LOCATION_SEARCH_CLOSED: 'location_search_closed',
  LOCATION_SEARCH_RESULT: 'location_search_result',
  FIELD_SELECTED: 'field_selected',
  FIELD_DRAW_COMPLETED: 'field_draw_completed',
  KML_UPLOAD_STARTED: 'kml_upload_started',
  KML_POLYGON_SELECTED: 'kml_polygon_selected',
  EMAIL_STARTED: 'email_started',
  PHONE_PROVIDED: 'phone_provided',
  EMAIL_SUBMITTED: 'email_submitted',
  REPORT_REQUESTED: 'report_requested',
  REPORT_SUCCESS: 'report_success',
  FORM_SUBMIT_ERROR: 'form_submit_error',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  DEMO_CTA_CLICKED: 'demo_cta_clicked',
  SIGNUP_CTA_CLICKED: 'signup_cta_clicked',
};

/** Clarity custom tags — session-scoped dimensions for filtering recordings */
const CLARITY_TAG_KEYS = new Set([
  'funnel_step',
  'location',
  'cta',
  'source',
  'depth',
  'hectares',
  'expanded',
]);

let clarityScriptInjected = false;
let dataCtaListenerAttached = false;

function getClarityProjectId() {
  return process.env.REACT_APP_CLARITY_PROJECT_ID || '';
}

function isClarityReady() {
  return typeof window !== 'undefined' && typeof window.clarity === 'function';
}

/**
 * Load Microsoft Clarity when REACT_APP_CLARITY_PROJECT_ID is set.
 * Safe to call multiple times.
 */
export function initClarity() {
  if (typeof window === 'undefined' || clarityScriptInjected) return;

  const projectId = getClarityProjectId();
  if (!projectId) return;

  clarityScriptInjected = true;

  /* eslint-disable no-unused-expressions */
  (function claritySnippet(c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function clarityQueue() {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = `https://www.clarity.ms/tag/${i}`;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', projectId);
  /* eslint-enable no-unused-expressions */

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[analytics] Clarity initialized', projectId);
  }
}

function sendToClarity(eventName, properties = {}) {
  if (!isClarityReady()) return;

  CLARITY_TAG_KEYS.forEach((key) => {
    const value = properties[key];
    if (value != null && value !== '') {
      window.clarity('set', key, String(value));
    }
  });

  window.clarity('event', eventName);
}

/**
 * @param {string} eventName
 * @param {Record<string, unknown>} [properties]
 */
export function trackEvent(eventName, properties = {}) {
  const payload = {
    event: eventName,
    timestamp: new Date().toISOString(),
    ...properties,
  };

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[analytics]', payload);
  }

  sendToClarity(eventName, properties);

  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, properties);
  }

  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(payload);
  }
}

/** @param {string} step - One of FunnelSteps values */
export function trackFunnelStep(step, properties = {}) {
  trackEvent(step, { funnel_step: step, ...properties });
}

/** Track scroll depth milestones (25, 50, 75, 100) */
export function initScrollDepthTracking() {
  if (typeof window === 'undefined') return () => {};

  const milestones = new Set();
  const handler = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const pct = Math.round((scrollTop / docHeight) * 100);
    [25, 50, 75, 100].forEach((m) => {
      if (pct >= m && !milestones.has(m)) {
        milestones.add(m);
        trackEvent('scroll_depth', { depth: m, funnel_step: 'scroll_depth' });
      }
    });
  };

  window.addEventListener('scroll', handler, { passive: true });
  return () => window.removeEventListener('scroll', handler);
}

/**
 * Fire once when key funnel sections enter the viewport.
 * @param {Array<{ id: string, event: string }>} sections
 */
export function initSectionViewTracking(sections) {
  if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
    return () => {};
  }

  const seen = new Set();
  const observers = sections
    .map(({ id, event }) => {
      const element = document.getElementById(id);
      if (!element) return null;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !seen.has(id)) {
            seen.add(id);
            trackFunnelStep(event, { section_id: id });
          }
        },
        { threshold: 0.25 },
      );

      observer.observe(element);
      return observer;
    })
    .filter(Boolean);

  return () => observers.forEach((observer) => observer.disconnect());
}

/** Catch-all for elements with data-cta (covers map expand, PDF links, etc.) */
export function initDataCtaTracking() {
  if (typeof document === 'undefined' || dataCtaListenerAttached) return;
  dataCtaListenerAttached = true;

  document.addEventListener(
    'click',
    (e) => {
      const target = e.target.closest('[data-cta]');
      if (!target) return;
      const cta = target.getAttribute('data-cta');
      if (!cta) return;
      trackEvent('data_cta_click', { cta, location: cta, funnel_step: 'data_cta_click' });
    },
    true,
  );
}

/** Initialize all analytics runtimes (call once on app mount). */
export function initAnalytics() {
  initClarity();
  initDataCtaTracking();
}
